import { Check, ChevronsUpDown, Plus } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export function Combobox<OptionType extends { label: string, value: any }>({
  placeholder,
  options,
  onSelect,
  onSelectLabel,
  onSelectOption,
  onCreateNew,
  defaultLabel,
  className,
  emptyMessage = "No results found.",
  createLabel = "Create",
  allowCreate = false,
  modal,
  id,
  disabled,
}: {
  placeholder: string,
  options?: OptionType[],
  onSelect?: (value: OptionType["value"]) => void,
  onSelectLabel?: (label: string) => void,
  onSelectOption?: (option: OptionType) => void,
  onCreateNew?: (searchValue: string) => Promise<OptionType> | void,
  defaultLabel?: string,
  className?: string,
  emptyMessage?: string,
  createLabel?: string,
  allowCreate?: boolean,
  modal?: boolean,
  id?: string,
  disabled?: boolean,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const [label, setLabel] = React.useState<string | undefined>(defaultLabel);

  // Update label when defaultLabel changes
  React.useEffect(() => {
    if (defaultLabel) {
      setLabel(defaultLabel);
    }
  }, [defaultLabel]);

  const handleCreateNew = async () => {
    if (!onCreateNew || !searchValue) return;

    const newOption = await onCreateNew(searchValue);
    if (newOption) {
      setLabel(newOption.label);
      setOpen(false);
      onSelect?.(newOption.value);
      onSelectLabel?.(newOption.label);
      onSelectOption?.(newOption);
      setSearchValue("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal={modal}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between font-normal", className)}
          disabled={disabled}
        >
          {label || placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" side="bottom" align="start">
        <Command>
          <CommandInput
            placeholder={placeholder}
            value={searchValue}
            onValueChange={setSearchValue}
          />
          <CommandList>
            <CommandEmpty>
              {allowCreate && searchValue ? (
                <div
                  className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none"
                  onClick={handleCreateNew}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {createLabel} "{searchValue}"
                </div>
              ) : (
                emptyMessage
              )}
            </CommandEmpty>
            <CommandGroup>
              {(options || []).map((option, index) => (
                <CommandItem
                  key={`${option.value}-${index}`}
                  value={option.label}
                  onSelect={() => {
                    setLabel(option.label);
                    setOpen(false);
                    setSearchValue("");
                    onSelect?.(option.value);
                    onSelectLabel?.(option.label);
                    onSelectOption?.(option);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      label === option.label ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
